/**
 * Renders the portfolio-ranking composite identities from the ONE declaration.
 *
 * The templates come from `./generated/portfolioIdentityFormats`, which is
 * generated from `forest_models/forest_models/contracts/portfolio_identity_formats.json`.
 * Django mints these identities from that same JSON, the FastAPI gateway re-mints
 * them from it when it overrides a record with a pending offer, and
 * `forest_models` validates them against it — so a format change is a one-line
 * edit in the contract instead of six hand-mirrored copies that silently drift
 * and make this client reject the whole envelope.
 *
 * Values are substituted VERBATIM. These are wire identities compared byte for
 * byte, not URLs: percent-encoding one of them would stop matching every
 * already-minted identity. `encodeRouteValue` from `portfolioPrimaryActionRoute`
 * is deliberately NOT used here, and the contract's golden cases pin the
 * difference in both languages.
 */
import {
  PORTFOLIO_IDENTITY_FORMAT_TEMPLATES,
  type PortfolioIdentityFormat,
} from './generated/portfolioIdentityFormats';

export type { PortfolioIdentityFormat };

const PLACEHOLDER = /\{([a-z_]+)\}/g;

/**
 * Render one declared identity format.
 *
 * Every placeholder the template names must be supplied as a non-blank string. A
 * missing or blank value throws instead of rendering a partial identity: a blank
 * segment yields a well-formed-looking string that silently fails the strict
 * comparison in `portfolioRankingContract`, which is the exact failure mode this
 * contract exists to prevent.
 */
export function renderPortfolioIdentity(
  format: PortfolioIdentityFormat,
  values: Readonly<Record<string, string>>,
): string {
  const template = PORTFOLIO_IDENTITY_FORMAT_TEMPLATES[format];
  if (template === undefined) {
    throw new Error(
      `Unknown portfolio identity format "${format}"; the identity contract declares `
      + `${Object.keys(PORTFOLIO_IDENTITY_FORMAT_TEMPLATES).join(', ')}.`,
    );
  }
  const used = new Set<string>();
  const rendered = template.replace(PLACEHOLDER, (_match, name: string) => {
    const value = values[name];
    if (typeof value !== 'string' || value.trim().length === 0) {
      throw new Error(
        `Identity template for "${format}" requires a non-empty "${name}"; `
        + `caller supplied ${JSON.stringify(values[name])}.`,
      );
    }
    used.add(name);
    return value;
  });
  const unused = Object.keys(values).filter((name) => !used.has(name));
  if (unused.length > 0) {
    throw new Error(
      `Identity template for "${format}" does not use ${unused.join(', ')}; `
      + 'supplying it means the caller and the contract disagree.',
    );
  }
  return rendered;
}

export interface PortfolioJobIdentityInput {
  readonly propertyId: string;
  readonly primaryJob: string;
  readonly signalRevision: string;
}

/** The served `job_id` binding property, ranked job and source snapshot. */
export function portfolioJobId(input: PortfolioJobIdentityInput): string {
  return renderPortfolioIdentity('portfolio_job_id', {
    property_id: input.propertyId,
    primary_job: input.primaryJob,
    signal_revision: input.signalRevision,
  });
}

/** The served per-property `record_id`. */
export function portfolioRecordId(propertyId: string): string {
  return renderPortfolioIdentity('portfolio_record_id', {
    property_id: propertyId,
  });
}

/** The public revision / `jobRef` for one Offer Decode job. */
export function offerDecodeRevision(decodeJobId: string): string {
  return renderPortfolioIdentity('offer_decode_revision', {
    decode_job_id: decodeJobId,
  });
}

/**
 * The literal head of the offer-decode revision template.
 *
 * DERIVED from the template, never declared a second time: this is the prefix a
 * landing hand-off URL carries, and a separately typed constant would be one
 * more copy to drift.
 */
export function offerDecodeJobPrefix(): string {
  const template = PORTFOLIO_IDENTITY_FORMAT_TEMPLATES.offer_decode_revision;
  const head = template.slice(0, template.indexOf('{'));
  if (!head) {
    throw new Error(
      `The "offer_decode_revision" template "${template}" has no literal prefix `
      + 'before its first placeholder; the hand-off parse direction depends on one.',
    );
  }
  return head;
}

/**
 * Recover the raw decode job id from a public offer-decode reference.
 *
 * Values without the prefix are returned unchanged: hand-off URLs have always
 * accepted both the prefixed reference and the bare job id. That is the wire
 * behaviour, not a fallback for missing data.
 */
export function stripOfferDecodeJobPrefix(value: string): string {
  const prefix = offerDecodeJobPrefix();
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}
